import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { HelpCircle } from 'lucide-react';
import AskDeveloperModal from './AskDeveloperModal';
import FaqModal from './FaqModal';
import { shouldShowHelpMenu } from './helpMenuVisibility';
import { useStationHelpBranding } from './helpStationBranding';
import { useAuth } from '../auth/useAuth';
import { useHelpCompletedNotification } from './useHelpNotifications';
import { listMyTickets } from '../../../services/helpTicketService';
import { tintFromAccent } from '../steps/_shared';

const RESERVED_ROOT_SEGMENTS = new Set([
  'admin',
  'invitations',
  'access-denied',
  'reset-password',
  'settings',
  'my-campaigns',
  'my-team',
]);

const HelpMenu: React.FC = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [askOpen, setAskOpen] = useState(false);
  const [faqOpen, setFaqOpen] = useState(false);
  const { slug: paramSlug } = useParams<{ slug?: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { hasUnreadCompleted, markCompletedSeen } = useHelpCompletedNotification(user?.id);

  const slug = useMemo(() => {
    if (paramSlug) return paramSlug;
    const match = location.pathname.match(/^\/([^/]+)/);
    if (!match) return undefined;
    const first = match[1];
    return RESERVED_ROOT_SEGMENTS.has(first) ? undefined : first;
  }, [paramSlug, location.pathname]);

  const { accent, primary } = useStationHelpBranding(slug);

  if (!shouldShowHelpMenu(location.pathname)) return null;

  const openHelpCenter = async () => {
    setMenuOpen(false);
    markCompletedSeen();

    let targetSlug = slug;
    if (!targetSlug && user) {
      try {
        const tickets = await listMyTickets();
        targetSlug = tickets[0]?.stationSlug;
      } catch {
        // Fall through — no slug means we can't render the branded help page.
      }
    }

    if (targetSlug) navigate(`/${targetSlug}/help`);
  };

  const openAsk = () => {
    setMenuOpen(false);
    setAskOpen(true);
  };

  const openFaq = () => {
    setMenuOpen(false);
    setFaqOpen(true);
  };

  const menuItemClass =
    'block w-full text-left px-4 py-2.5 text-sm text-slate-800 transition-colors';

  return createPortal(
    <>
      <AskDeveloperModal open={askOpen} onClose={() => setAskOpen(false)} />
      <FaqModal open={faqOpen} onClose={() => setFaqOpen(false)} stationSlug={slug} />
      <div className="fixed bottom-4 right-4 max-sm:bottom-[4.5rem] z-50 flex flex-col items-end gap-2">
        {menuOpen && (
          <div
            className="rounded-xl bg-white shadow-lg border py-1 min-w-[220px] overflow-hidden"
            style={accent ? { borderColor: tintFromAccent(accent, 0.2) } : { borderColor: '#e2e8f0' }}
          >
            <button
              type="button"
              onClick={openFaq}
              className={`${menuItemClass} hover:bg-slate-50`}
            >
              Frequently asked questions
            </button>
            <button
              type="button"
              onClick={openAsk}
              className={`${menuItemClass} hover:bg-slate-50`}
            >
              Ask our support team
            </button>
            <button
              type="button"
              onClick={() => void openHelpCenter()}
              className={`${menuItemClass} border-t border-slate-100 hover:bg-slate-50`}
            >
              My tickets
            </button>
          </div>
        )}
        <div className="relative">
          {hasUnreadCompleted && (
            <span
              className="absolute -top-0.5 -right-0.5 h-3 w-3 rounded-full bg-red-500 border-2 border-white"
              aria-hidden
            />
          )}
          {accent && primary ? (
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Help"
              aria-expanded={menuOpen}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full text-white shadow-md transition-all hover:brightness-110"
              style={{
                background: `linear-gradient(180deg, ${accent}, ${primary})`,
                boxShadow: `0 6px 14px -8px ${primary}`,
              }}
            >
              <HelpCircle className="h-6 w-6" />
            </button>
          ) : (
            <button
              type="button"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Help"
              aria-expanded={menuOpen}
              className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-slate-900 text-white shadow-md hover:bg-slate-700 transition-colors"
            >
              <HelpCircle className="h-6 w-6" />
            </button>
          )}
        </div>
      </div>
    </>,
    document.body,
  );
};

export default HelpMenu;
